import {ChangeEvent, useCallback, useReducer} from 'react';
import {IAlert} from '../../../../../actions/proto';
import {priority} from '@priority-software/priority-proto-api/lib';
import {AlertMappedItem} from '../../../../../reducer/types';
import {AlertFields} from '../../../../../enums';
import {emptyRecipientFields} from '../../../../../constants';
import {messageLangs} from './constants';
import {getActiveMessageLang} from './utils';

type Props = {
    openRule: AlertMappedItem;
    onRuleUpdate: (data: Partial<IAlert>) => void;
};

export enum ActionTypes {
    CHANGE_MESSAGE = 'CHANGE_MESSAGE',
    CHANGE_MESSAGE_LANG = 'CHANGE_MESSAGE_LANG',
    UPDATE_RULE_FIELD = 'UPDATE_RULE_FIELD'
}

export const businessRuleTemplateReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.CHANGE_MESSAGE_LANG:
            return {...state, activeMessageLang: action.payload.activeMessageLang};

        case ActionTypes.UPDATE_RULE_FIELD:
            return {...state, ...action.payload.data};

        case ActionTypes.CHANGE_MESSAGE:
            const currLang = messageLangs[state.activeMessageLang];
            const currMessage =
                state.Messages[currLang] ||
                new priority.netitems.form.AlertMessage({
                    Message: '',
                    Language: state.activeMessageLang,
                    Subject: ''
                });

            const Messages = {
                ...state.Messages,
                [currLang]: {
                    ...currMessage,
                    [action.payload.field]: action.payload.value
                }
            };
            action.onRuleUpdate({Messages: Object.values(Messages)});

            return {...state, Messages};
        default:
            return state;
    }
};

type IAlertMessage = priority.netitems.form.IAlertMessage;

type State = {
    Messages: {[key: number]: IAlertMessage};
    activeMessageLang: string;
    MailType?: number | null;
    WebHook?: number | null;
    RecipientMail?: string[];
    RecipientField?: number | null;
    RecipientGroup?: (number | Long)[] | null;
    RecipientUser?: (number | Long)[] | null;
    ExtFile?: boolean;
    Pdf?: boolean;
    WordTemplates?: boolean;
    Document?: number | Long | null;
    DocumentFormat?: number | Long | null;
    ToStatus?: number | Long | null;
    CoverLetters?: {[key: number]: IAlertMessage};
};

type Action<T, P = undefined, C = undefined> = {
    type: T;
    payload?: P;
    onRuleUpdate?: C;
};

type BusinessRuleTemplateAction =
    | Action<
          ActionTypes.CHANGE_MESSAGE,
          {field: string; value: string | number | boolean | string[]},
          (data: Partial<IAlert>) => void
      >
    | Action<ActionTypes.CHANGE_MESSAGE_LANG, {activeMessageLang: string}>
    | Action<
          ActionTypes.UPDATE_RULE_FIELD,
          {data: Partial<State>},
          (data: Partial<IAlert>) => void
      >;

type ReducerType = (state: State, action: BusinessRuleTemplateAction) => State;

export const useBusinessRuleTemplatesData = (props: Props) => {
    const {openRule, onRuleUpdate} = props;

    const createSendEMail = (
        ruleKey: priority.netitems.form.IAlertMessage[]
    ): {[key: number]: IAlertMessage} => {
        return ruleKey.reduce((prev, curr) => {
            return {...prev, [messageLangs[Number(curr.Language)]]: curr};
        }, {}) as {[key: number]: IAlertMessage};
    };

    const [state, dispatch] = useReducer<ReducerType, State>(
        businessRuleTemplateReducer,
        {
            Messages: openRule.Messages.reduce((prev, curr) => {
                return {...prev, [messageLangs[Number(curr.Language)]]: curr};
            }, {}) as {[key: number]: IAlertMessage},
            activeMessageLang: getActiveMessageLang(),
            MailType: Number(openRule.MailType),
            WebHook: Number(openRule.WebHook),
            RecipientMail: openRule.RecipientMail,
            RecipientField: Number(openRule.RecipientField),
            RecipientGroup: openRule.RecipientGroup,
            RecipientUser: openRule.RecipientUser,
            ExtFile: openRule.ExtFile,
            Pdf: openRule.Pdf,
            WordTemplates: openRule.WordTemplates,
            Document: openRule.Document,
            DocumentFormat: openRule.DocumentFormat,
            ToStatus: openRule?.BPM?.ToStatus,
            CoverLetters: createSendEMail(openRule.CoverLetters)
        },
        arg => arg
    );

    const emails = state.RecipientMail;

    const currentMessageData: priority.netitems.form.IAlertMessage =
        state.Messages[messageLangs[state.activeMessageLang]];

    const handleMessageData = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const field = e.target.name;
            const value = e.target.value;

            dispatch({type: ActionTypes.CHANGE_MESSAGE, payload: {field, value}, onRuleUpdate});
        },
        []
    );

    const insertShortcodeData = useCallback((value: string, fieldName: string) => {
        dispatch({
            type: ActionTypes.CHANGE_MESSAGE,
            payload: {field: fieldName, value},
            onRuleUpdate
        });
    }, []);

    const onMessageLangChange = useCallback((activeMessageLang: string) => {
        dispatch({type: ActionTypes.CHANGE_MESSAGE_LANG, payload: {activeMessageLang}});
    }, []);

    const updateRuleField = useCallback(
        (value: string | number | boolean | Array<string>, field: string) => {
            let payload = {[field]: value};
            if (field === AlertFields.MailType) {
                payload = {...emptyRecipientFields, ...payload};
            }
            dispatch({type: ActionTypes.UPDATE_RULE_FIELD, payload: {data: payload}});
            onRuleUpdate(payload);
        },
        [onRuleUpdate]
    );

    const addEmailValues = useCallback(
        (email: string) => {
            updateRuleField(Array.from(new Set([...emails, email])), AlertFields.RecipientMail);
        },
        [updateRuleField, emails]
    );

    const removeEmailValue = useCallback(
        (email: string) => {
            updateRuleField(
                emails.filter(item => email !== item),
                AlertFields.RecipientMail
            );
        },
        [updateRuleField, emails]
    );

    const BPM = openRule.BPM;

    const changeStatus = useCallback(
        (data: number, name: string) => {
            const bpm = {BPM: {...(BPM || {}), [name]: data}};
            dispatch({type: ActionTypes.UPDATE_RULE_FIELD, payload: {data: {[name]: data}}});
            onRuleUpdate(bpm);
        },
        [updateRuleField, BPM, onRuleUpdate]
    );

    const changeCoverLetter = useCallback(
        (coverLetter: {[key: number]: IAlertMessage}) => {
            dispatch({
                type: ActionTypes.UPDATE_RULE_FIELD,
                payload: {data: {CoverLetters: coverLetter}}
            });
        },
        [onRuleUpdate]
    );

    return {
        state: {...state, currentMessageData},
        handlers: {
            handleMessageData,
            onMessageLangChange,
            insertShortcodeData,
            updateRuleField,
            addEmailValues,
            removeEmailValue,
            changeStatus,
            changeCoverLetter
        }
    };
};
